package chand.wordvoyage.navigation

import androidx.compose.runtime.*
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.platform.LocalUriHandler
import chand.wordvoyage.GenerateWordScreen
import chand.wordvoyage.LoginScreen
import chand.wordvoyage.WordDetail
import chand.wordvoyage.WordHistoryDetailScreen
import chand.wordvoyage.WordHistoryScreen
import chand.wordvoyage.data.WordEntry
import chand.wordvoyage.platform.createAuthRepository
import chand.wordvoyage.platform.createFirestoreRepository
import chand.wordvoyage.repository.AuthState
import kotlinx.coroutines.launch

// Navigation routes sealed class for type-safe navigation
sealed class NavDestination {
    object Login : NavDestination()
    object GenerateWord : NavDestination()
    object WordHistory : NavDestination()
    data class WordHistoryDetail(val entry: WordEntry) : NavDestination()
    data class WordDetail(val word: String, val userId: String) : NavDestination()
}

/**
 * AppNavigation handles screen navigation using Compose state management.
 * Flow: LoginScreen → GenerateWordScreen ⇄ WordHistoryScreen
 *                              ↓
 *                       WordDetailScreen
 */
@Composable
fun AppNavigation() {
    val authRepository = remember { createAuthRepository() }
    val firestoreRepository = remember { createFirestoreRepository() }

    // Determine the initial screen based on auth state.
    val initialDestination = remember {
        runCatching {
            val user = authRepository.getCurrentUser()
            if (user is AuthState.Authenticated) {
                NavDestination.GenerateWord
            } else {
                NavDestination.Login
            }
        }.getOrElse { error ->
            println("Failed to resolve initial navigation destination: ${error.message}")
            NavDestination.Login
        }
    }
    val currentDestination = remember { mutableStateOf(initialDestination) }
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current
    var showDeleteAccountDialog by remember { mutableStateOf(false) }

    val privacyPolicyUrl = "https://wordvoyage-c684a.web.app/privacy.html"
    val childrensPrivacyUrl = "https://wordvoyage-c684a.web.app/childrens-privacy.html"
    val dataControlDeletionUrl = "https://wordvoyage-c684a.web.app/data-control-deletion.html"

    if (showDeleteAccountDialog) {
        val user = authRepository.getCurrentUser() as? AuthState.Authenticated
        AlertDialog(
            onDismissRequest = { showDeleteAccountDialog = false },
            title = { Text("Delete Account?", style = MaterialTheme.typography.titleLarge) },
            text = { Text("This will permanently delete your account and all your word history. This action cannot be undone.", style = MaterialTheme.typography.bodyMedium) },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteAccountDialog = false
                        if (user != null) {
                            scope.launch {
                                // 1. Delete Firestore data
                                firestoreRepository.deleteAllUserData(user.userId)
                                // 2. Delete Auth account
                                authRepository.deleteAccount()
                                // 3. Redirect to login
                                currentDestination.value = NavDestination.Login
                            }
                        }
                    },
                    colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Delete Everything", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteAccountDialog = false }) {
                    Text("Cancel")
                }
            }
        )
    }

    when (val destination = currentDestination.value) {
        is NavDestination.Login -> {
            LoginScreen(
                onLoginSuccess = { _ ->
                    currentDestination.value = NavDestination.GenerateWord
                }
            )
        }
        is NavDestination.GenerateWord -> {
            val userId = (authRepository.getCurrentUser() as? AuthState.Authenticated)?.userId ?: ""
            GenerateWordScreen(
                onShowWordDetail = { word ->
                    currentDestination.value = NavDestination.WordDetail(word, userId)
                },
                onNavigateToHistory = {
                    currentDestination.value = NavDestination.WordHistory
                },
                onLogout = {
                    authRepository.signOut()
                    currentDestination.value = NavDestination.Login
                },
                onDeleteAccount = {
                    showDeleteAccountDialog = true
                },
                onViewPrivacyPolicy = {
                    uriHandler.openUri(privacyPolicyUrl)
                },
                onViewChildrensPrivacy = {
                    uriHandler.openUri(childrensPrivacyUrl)
                },
                onViewDataControlDeletion = {
                    uriHandler.openUri(dataControlDeletionUrl)
                },
                userId = userId,
                firestoreRepository = firestoreRepository
            )
        }
        is NavDestination.WordHistory -> {
            val userId = (authRepository.getCurrentUser() as? AuthState.Authenticated)?.userId ?: ""
            WordHistoryScreen(
                userId = userId,
                onBack = {
                    currentDestination.value = NavDestination.GenerateWord
                },
                onDeleteAccount = {
                    showDeleteAccountDialog = true
                },
                onViewPrivacyPolicy = {
                    uriHandler.openUri(privacyPolicyUrl)
                },
                onViewChildrensPrivacy = {
                    uriHandler.openUri(childrensPrivacyUrl)
                },
                onViewDataControlDeletion = {
                    uriHandler.openUri(dataControlDeletionUrl)
                },
                onItemClick = { entry ->
                    currentDestination.value = NavDestination.WordHistoryDetail(entry)
                }
            )
        }
        is NavDestination.WordHistoryDetail -> {
            // Displays saved word data from Firestore — no OpenAI call
            WordHistoryDetailScreen(
                entry = destination.entry,
                onBack = {
                    currentDestination.value = NavDestination.WordHistory
                }
            )
        }
        is NavDestination.WordDetail -> {
            WordDetail(
                word = destination.word,
                userId = destination.userId,
                onClose = {
                    currentDestination.value = NavDestination.GenerateWord
                }
            )
        }
    }
}
